import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ProductForm } from './ProductForm';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  PencilIcon,
  EyeIcon,
  TagIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { ProductVariants } from '../../components/pim/ProductVariants';
import { usePermissions } from '../../hooks/usePermissions';
import { useNotification } from '../../contexts/NotificationContext';
import { pimService } from '../../services/pimService';
import type { Product, ProductStatus, PimAttribute } from '../../types/pim';

const AttributeValueDisplay: React.FC<{
  attribute: PimAttribute;
  value: any;
}> = ({ attribute, value }) => {
  const { t } = useTranslation();

  if (value === null || value === undefined) {
    return <span className="text-gray-400">-</span>;
  }

  const renderValue = () => {
    switch (attribute.type) {
      case 'boolean':
        return value ? t('common.yes') : t('common.no');
      
      case 'date':
        return new Date(value).toLocaleDateString();
      
      case 'multiselect':
        return Array.isArray(value) ? value.join(', ') : value;
      
      case 'json':
        return (
          <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
            {JSON.stringify(value, null, 2)}
          </pre>
        );
      
      case 'text':
        return (
          <div className="whitespace-pre-wrap">
            {value}
          </div>
        );
      
      default:
        return String(value);
    }
  };

  return (
    <div>
      {renderValue()}
    </div>
  );
};

export const ProductDetail = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const permissions = usePermissions();
  const { showNotification } = useNotification();

  const [/*isEditing*/] = useState<boolean>(true);

  const { data: productData, isLoading, error } = useQuery({
    queryKey: ['pim-product', id],
    queryFn: () => pimService.getProduct(parseInt(id!)),
    enabled: Boolean(id),
    onError: (error: any) => {
      if (error.response?.status === 404) {
        showNotification('Produkt nie został znaleziony.', 'error');
      } else if (error.response?.status === 500) {
        showNotification('Błąd serwera podczas ładowania szczegółów produktu.', 'error');
      } else {
        showNotification('Błąd podczas ładowania szczegółów produktu.', 'error');
      }
    }
  });
  const [translations, setTranslations] = useState<Array<{locale: string; name: string; description?: string; slug?: string}>>([]);
  const [selectedLocale, setSelectedLocale] = useState<string>((i18n.language || 'en').slice(0,2));
  const [previewMode, setPreviewMode] = useState<boolean>(false);
  const [allowedTags, setAllowedTags] = useState<string[]>(['instrukcja','karta','certyfikat']);
  const supportedLocales = ['en','de','fr','es','pl','it'];


  useEffect(() => {
    const fetchTranslations = async () => {
      if (!id) return;
      try {
        const resp = await pimService.getProductTranslations(parseInt(id));
        setTranslations(resp.data || []);
      } catch {}
    };
    fetchTranslations();
  }, [id]);

  // Reaguj na zmianę języka aplikacji – podpowiadaj locale widoku
  useEffect(() => {
    const lang = (i18n.language || 'en').slice(0,2);
    // Nie nadpisuj, jeśli user zmienił ręcznie; tylko jeśli pusta wartość
    setSelectedLocale(prev => prev || lang);
  }, [i18n.language]);

  useEffect(() => {
    const run = async () => {
      try {
        const tags = await pimService.getAllowedMediaTags();
        if (Array.isArray(tags) && tags.length) setAllowedTags(tags);
      } catch {}
    };
    run();
  }, []);

  useEffect(() => {
    if (window.location.hash === '#media') {
      const el = document.getElementById('media-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const getStatusBadge = (status: ProductStatus) => {
    switch (status) {
      case 'published':
        return <Badge variant="success" icon={CheckCircleIcon}>Published</Badge>;
      case 'draft':
        return <Badge variant="warning" icon={ClockIcon}>Draft</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <p className="mt-2 text-gray-600">Loading product...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading product: {(error as any).message}</p>
      </div>
    );
  }

  if (!productData?.data) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Product not found.</p>
      </div>
    );
  }

  const product = productData.data;

  // Group attributes by their groups
  const attributeGroups = product.attributeValues?.reduce((groups, attributeValue) => {
    if (!attributeValue.attribute) return groups;
    
    const groupId = attributeValue.attribute.group_id;
    if (!groups[groupId]) {
      groups[groupId] = {
        group: attributeValue.attribute.group,
        attributes: []
      };
    }
    
    groups[groupId].attributes.push({
      attribute: attributeValue.attribute,
      value: getAttributeValue(attributeValue)
    });
    
    return groups;
  }, {} as Record<number, { group?: any; attributes: { attribute: PimAttribute; value: any }[] }>) || {};

  function getAttributeValue(attributeValue: any) {
    const attribute = attributeValue.attribute;
    switch (attribute.type) {
      case 'string':
      case 'enum':
        return attributeValue.value_string;
      case 'text':
        return attributeValue.value_text;
      case 'number':
        return attributeValue.value_number;
      case 'boolean':
        return attributeValue.value_boolean;
      case 'date':
        return attributeValue.value_date;
      case 'json':
      case 'multiselect':
        return attributeValue.value_json;
      default:
        return null;
    }
  }

  return (
    <div className="space-y-6">
      {/* Full edit form */}
      <div className="mt-4">
        <div className="border rounded-lg p-4">
          <ProductForm productId={id} />
        </div>
      </div>

      {/* View-only basic info removed to keep single full-edit experience */}

      {/* Translations */}
      <Card>
        <CardHeader>
          <CardTitle>Translations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 mb-4">
            <label className="text-sm text-gray-700">Locale</label>
            <select
              value={selectedLocale}
              onChange={(e) => setSelectedLocale(e.target.value)}
              className="border rounded px-2 py-1"
            >
              {supportedLocales.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
            <label className="ml-4 text-sm text-gray-700 flex items-center gap-2">
              <input type="checkbox" checked={previewMode} onChange={(e) => setPreviewMode(e.target.checked)} />
              Preview
            </label>
            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={async () => {
                  try {
                    const blob = await pimService.exportProductTranslations(parseInt(id!), 'json');
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `product-${id}-translations.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch {
                    showNotification('Nie udało się wyeksportować tłumaczeń (JSON)', 'error');
                  }
                }}
              >Export JSON</Button>
              <Button
                variant="secondary"
                onClick={async () => {
                  try {
                    const blob = await pimService.exportProductTranslations(parseInt(id!), 'csv');
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `product-${id}-translations.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch {
                    showNotification('Nie udało się wyeksportować tłumaczeń (CSV)', 'error');
                  }
                }}
              >Export CSV</Button>
              <label className="cursor-pointer text-sm px-3 py-2 bg-gray-100 rounded hover:bg-gray-200">
                Import
                <input
                  type="file"
                  accept=".json,.csv,application/json,text/csv"
                  className="hidden"
                  onChange={async (e) => {
                    if (!e.target.files || !e.target.files[0]) return;
                    const file = e.target.files[0];
                    try {
                      const resp = await pimService.importProductTranslations(parseInt(id!), file);
                      showNotification(`Import zakończony. Dodano: ${resp.data?.created || 0}, Zaktualizowano: ${resp.data?.updated || 0}${resp.data?.errors?.length ? `, Błędy: ${resp.data.errors.length}` : ''}`,'success');
                      const r = await pimService.getProductTranslations(parseInt(id!));
                      setTranslations(r.data || []);
                    } catch {
                      showNotification('Nie udało się zaimportować tłumaczeń', 'error');
                    } finally {
                      e.currentTarget.value = '';
                    }
                  }}
                />
              </label>
            </div>
          </div>
          {(() => {
            const existing = translations.find(t => t.locale === selectedLocale) || { locale: selectedLocale, name: '', description: '', slug: '' };
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name ({selectedLocale})</label>
                  <input
                    type="text"
                    defaultValue={existing.name}
                    className="w-full border rounded px-3 py-2"
                    onBlur={async (e) => {
                      const name = e.target.value;
                      if (!name) return;
                      try {
                        await pimService.upsertProductTranslation(parseInt(id!), selectedLocale, { name, description: existing.description, slug: existing.slug });
                        setTranslations(prev => {
                          const rest = prev.filter(t => t.locale !== selectedLocale);
                          return [...rest, { ...existing, name }];
                        });
                        showNotification('Zapisano tłumaczenie nazwy', 'success');
                      } catch {
                        showNotification('Nie udało się zapisać tłumaczenia', 'error');
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug ({selectedLocale})</label>
                  <input
                    type="text"
                    defaultValue={existing.slug}
                    className="w-full border rounded px-3 py-2"
                    onBlur={async (e) => {
                      const slug = e.target.value.trim();
                      if (!slug) return;
                      try {
                        const v = await pimService.validateTranslationSlug(selectedLocale, slug, parseInt(id!));
                        if (!v.available) {
                          showNotification('Slug jest zajęty w tym języku', 'error');
                          return;
                        }
                        await pimService.upsertProductTranslation(parseInt(id!), selectedLocale, { name: existing.name || product.name, description: existing.description, slug });
                        setTranslations(prev => {
                          const rest = prev.filter(t => t.locale !== selectedLocale);
                          return [...rest, { ...existing, slug }];
                        });
                        showNotification('Zapisano slug', 'success');
                      } catch {
                        showNotification('Nie udało się zapisać slug', 'error');
                      }
                    }}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description ({selectedLocale})</label>
                  <textarea
                    defaultValue={existing.description}
                    className="w-full border rounded px-3 py-2"
                    rows={4}
                    onBlur={async (e) => {
                      const description = e.target.value;
                      try {
                        await pimService.upsertProductTranslation(parseInt(id!), selectedLocale, { name: existing.name || product.name, description, slug: existing.slug });
                        setTranslations(prev => {
                          const rest = prev.filter(t => t.locale !== selectedLocale);
                          return [...rest, { ...existing, description }];
                        });
                        showNotification('Zapisano opis', 'success');
                      } catch {
                        showNotification('Nie udało się zapisać opisu', 'error');
                      }
                    }}
                  />
                </div>
                {previewMode && (
                  <div className="md:col-span-2 mt-2">
                    <div className="border rounded p-4 bg-gray-50">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Preview: {selectedLocale}</h4>
                      <div className="space-y-2">
                        <div><span className="text-xs uppercase text-gray-500">Name</span><div className="text-gray-900">{existing.name || product.name}</div></div>
                        {(existing.slug || '').trim() && (
                          <div><span className="text-xs uppercase text-gray-500">Slug</span><div className="text-gray-900">{existing.slug}</div></div>
                        )}
                        {(existing.description || '').trim() && (
                          <div><span className="text-xs uppercase text-gray-500">Description</span><div className="text-gray-900 whitespace-pre-wrap">{existing.description}</div></div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div className="md:col-span-2 flex justify-end">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        await pimService.deleteProductTranslation(parseInt(id!), selectedLocale);
                        setTranslations(prev => prev.filter(t => t.locale !== selectedLocale));
                        showNotification('Usunięto tłumaczenie', 'success');
                      } catch {
                        showNotification('Nie udało się usunąć tłumaczenia', 'error');
                      }
                    }}
                  >
                    Usuń tłumaczenie ({selectedLocale})
                  </Button>
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>


      {/* Attributes */}
      {Object.keys(attributeGroups).length > 0 && (
        <div className="space-y-6">
          {Object.values(attributeGroups).map(({ group, attributes }, index) => (
            <Card key={group?.id || `group-${index}`}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TagIcon className="h-5 w-5" />
                  {group?.name || 'Other Attributes'}
                </CardTitle>
                {group?.description && (
                  <p className="text-sm text-gray-600">{group.description}</p>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {attributes.map(({ attribute, value }) => (
                    <div key={attribute.id}>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-medium text-gray-700">
                          {attribute.label}
                        </h4>
                        {attribute.is_required && (
                          <span className="text-xs text-red-500">Required</span>
                        )}
                        {attribute.is_unique && (
                          <span className="text-xs text-blue-500">Unique</span>
                        )}
                      </div>
                      {attribute.description && (
                        <p className="text-xs text-gray-500 mb-2">{attribute.description}</p>
                      )}
                      <div className="text-gray-900">
                        <AttributeValueDisplay attribute={attribute} value={value} />
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Type: {attribute.type}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State for Attributes */}
      {Object.keys(attributeGroups).length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No Attributes</h3>
            <p className="mt-1 text-sm text-gray-500">
              This product doesn't have any custom attributes assigned.
            </p>
            {permissions.canWriteProducts && (
              <div className="mt-6">
                <Link to={`/pim/products/${product.id}/edit`}>
                  <Button icon={PencilIcon}>
                    Edit Product
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Product Variants */}
      <Card>
        <CardContent>
          <ProductVariants 
            productId={product.id} 
            canEdit={permissions.canWriteProducts} 
          />
        </CardContent>
      </Card>
    </div>
  );
};
